/**
 * Routeur API centralisé pour gérer tous les appels vers les différents workers
 * Basé sur le préfixe de l'URL, route les appels vers le bon worker
 * Système de headers restructuré : X-Routing-Data contient la config complète en base64
 */

import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import { encryptData, isEncrypted } from './encrypt-service.js';
import { getCircuitBreaker, checkWatchdog, isWatchdogActive } from './circuit-breaker.js';
import { CIRCUIT_STATES, STANDARD_STATUS_CODES } from '../constants/circuit-breaker-constants.js';
import { getTranslation } from '../context/TranslationContext.jsx';
import { cacheManager } from './cache-manager.js';
import { waSessionStorage } from './session-storage.js';
import { getRoutingConfig, getRoutingConfigEncrypted } from './routing-config-manager.js';
import { enrichConfigWithSecurity } from './http-security.js';
// Modules communs partagés avec les workers
import { getConfigForEndpoint } from '@commons-shared/routes-resolver.js';
import { parseResponse, classifyErrorType, extractErrorMessage } from '@commons-shared/response-handler.js';
// Configuration des routes
import { ROUTES_CONFIG, getRouteConfigByRoute } from '../config/routes-config.js';
// Import Handlebars pour la reconstruction des templates
import Handlebars from 'handlebars';

/**
 * Traite la réponse de /api/config/read/hb-compiled pour reconstruire le template compilé
 * Ajoute une propriété 'compiled' à chaque template en évaluant compiled_spec
 * 
 * @param {Object} data - Réponse de l'API avec {success, templates: [...]}
 * @returns {Promise<Object>} - Données avec templates enrichis
 */
async function processHbCompiledResponse(data) {
  try {
    const processedTemplates = [];
    
    for (const hbData of data.templates) {
      // Créer une copie du template
      const processedTemplate = { ...hbData };
      
      // Si le template est valide et a un compiled_spec, le reconstruire
      if (hbData.is_valid && hbData.compiled_spec) {
        try {
          // Évaluer le compiled_spec pour obtenir l'objet spec (utilisation de Function au lieu d'eval pour la sécurité)
          const spec = new Function(`return ${hbData.compiled_spec}`)();
          
          // Créer le template compilé Handlebars
          const compiledTemplate = Handlebars.template(spec);
          
          // Ajouter la propriété compiled
          processedTemplate.compiled = compiledTemplate;
          
          debug.log(config, `[API Router] Template ${hbData.source_key} reconstructed successfully`);
        } catch (error) {
          debug.error(config, `[API Router] Failed to reconstruct template ${hbData.source_key}:`, error);
          processedTemplate.compiled = null;
        }
      } else {
        processedTemplate.compiled = null;
      }
      
      processedTemplates.push(processedTemplate);
    }
    
    return {
      ...data,
      templates: processedTemplates
    };
  } catch (error) {
    debug.error(config, '[API Router] Error processing hb-compiled response:', error);
    return data; // Retourner les données originales en cas d'erreur
  }
}

/**
 * Encode une chaîne en base64
 * @param {string} str
 * @returns {string}
 */
function base64Encode(str) {
  return btoa(unescape(encodeURIComponent(str)));
}

/**
 * Construit et encode les données de routage pour les headers
 * Utilise la nouvelle structure JSON unifiée
 * 
 * @param {Object} routingConfig - Configuration complète de routage
 * @param {string} masterKey - Clé maître optionnelle pour signer
 * @returns {Promise<{encoded: string, signature: string|null}>}
 */
async function buildAndEncodeRoutingData(routingConfig, masterKey = null) {
  try {
    // Ajouter signature si masterKey disponible
    let configToSend = routingConfig;
    
    if (masterKey) {
      configToSend = await enrichConfigWithSecurity(routingConfig, masterKey);
    }
    
    // Encoder en base64
    const jsonString = JSON.stringify(configToSend);
    const encoded = base64Encode(jsonString);
    
    debug.log(config, 'Données de routage encodées et prêtes pour les headers');
    
    return {
      encoded,
      signature: configToSend._security?.signature || null
    };
  } catch (error) {
    debug.error(config, 'Erreur lors de l\'encodage des données de routage:', error);
    throw error;
  }
}

/**
 * Injecte les headers unifiés pour les requêtes API
 * Remplace les multiples headers individuels par un système centralisé
 * 
 * @param {string} endpoint - L'endpoint API
 * @param {Object} headers - Headers existants
 * @returns {Promise<Object>} - Headers avec X-Routing-Data et X-Application-Key
 */
async function injectRequestHeaders(endpoint, headers = {}) {
  const newHeaders = { ...headers };
  
  try {
    // Récupérer la configuration complète de routage
    const routingConfig = await getRoutingConfig();
    
    // Encoder les données de routage (optionnellement avec signature)
    const { encoded } = await buildAndEncodeRoutingData(routingConfig);
    
    // Ajouter les headers structurés
    newHeaders['X-Routing-Data'] = encoded;
    
    // Debug log
    debug.log(config, `Headers injectés pour endpoint: ${endpoint}`);
    
    return newHeaders;
  } catch (error) {
    debug.error(config, `Erreur lors de l'injection des headers pour ${endpoint}:`, error);
    // Retourner les headers tel quels en cas d'erreur
    return newHeaders;
  }
}

/**
 * Configuration des routes et leurs workers correspondants
 * Utilise getConfigForEndpoint pour obtenir la configuration complète
 */
export function getWorkerConfig(endpoint) {
  // Utiliser le module commun pour obtenir la config complète
  const fullConfig = getConfigForEndpoint(endpoint, ROUTES_CONFIG);
  
  if (fullConfig) {
    return {
      baseUrl: fullConfig.baseUrl,
      name: fullConfig.workerName,
      circuitRoute: fullConfig.circuitRoute
    };
  }

  return null;
}

/**
 * Détermine quel worker utiliser basé sur l'endpoint
 * @param {string} endpoint - L'endpoint API
 * @returns {Object} Configuration du worker (baseUrl, name)
 */

/**
 * Vérifie l'état du circuit breaker et lance une erreur si nécessaire
 * @param {string} circuitRoute - Route du circuit breaker
 * @returns {Promise<void>}
 * @throws {Error} Si le circuit est ouvert ou en cours de test
 */
async function checkCircuitBreakerState(circuitRoute, healthCheckMode = null) {
  const circuitBreaker = getCircuitBreaker(circuitRoute);
  
  if (!circuitBreaker) {
    debug.warn(config, `Aucun circuit breaker trouvé pour ${circuitRoute}`);
    return;
  }
  
  // Log détaillé pour debugger le problème de cooldown
  debug.log(config, `checkCircuitBreakerState appelé pour ${circuitRoute} avec mode: ${healthCheckMode}`);
  
  // Pour les health checks automatiques (INTERNAL), utiliser isWatchdogActive (sans enregistrer l'appel)
  // Pour les appels utilisateur normaux ou health checks USER, utiliser checkWatchdog (avec enregistrement)
  const isAutomaticHealthCheck = healthCheckMode === 'INTERNAL';
  const watchdogBlocked = isAutomaticHealthCheck 
    ? isWatchdogActive(circuitRoute)
    : checkWatchdog(circuitRoute);
    
  if (watchdogBlocked) {
    // Récupérer le nom du service depuis la configuration des routes
    const routeConfig = getRouteConfigByRoute(circuitRoute);
    const serviceName = routeConfig?.displayName || 'Service inconnu';
    
    const message = getTranslation(
      'CIRCUIT_BREAKER', 
      'WATCHDOG_ERROR', 
      `Service ${serviceName} : Trop d'appels rapides détectés. Veuillez patienter avant de réessayer.`,
      { serviceName }
    );
    
    debug.warn(config, `Watchdog déclenché pour ${serviceName}. Refus de l'appel.`);
    
    const error = new Error(message);
    error.name = 'WatchdogError';
    error.serviceName = serviceName;
    error.errorType = 'watchdog';
    error.userFriendly = true;
    
    throw error;
  }
  
  // Récupérer le nom du service depuis la configuration des routes
  const routeConfig = getRouteConfigByRoute(circuitRoute);
  const serviceName = routeConfig?.displayName || 'Service inconnu';
  
  const status = circuitBreaker.getStatus();
  
  if (status.state === CIRCUIT_STATES.OPEN) {
    let message;
    
    // Utiliser les traductions si disponibles, sinon fallback sur les messages par défaut
    switch (status.errorType) {
      case 'auth':
        message = getTranslation(
          'CIRCUIT_BREAKER', 
          'AUTH_ERROR', 
          `Service ${serviceName} : Problème d'authentification. Vérifiez vos clés API ou reconnectez-vous.`,
          { serviceName }
        );
        break;
      case 'connectivity':
        message = getTranslation(
          'CIRCUIT_BREAKER', 
          'CONNECTIVITY_ERROR', 
          `Service ${serviceName} temporairement indisponible (problème de connectivité). Veuillez réessayer dans quelques instants.`,
          { serviceName }
        );
        break;
      case 'service':
        message = getTranslation(
          'CIRCUIT_BREAKER', 
          'SERVICE_ERROR', 
          `Service ${serviceName} rencontre des difficultés techniques. Veuillez patienter pendant que nous résolvons le problème.`,
          { serviceName }
        );
        break;
      case 'timeout':
        message = getTranslation(
          'CIRCUIT_BREAKER', 
          'TIMEOUT_ERROR', 
          `Service ${serviceName} ne répond pas (timeout). Veuillez réessayer plus tard.`,
          { serviceName }
        );
        break;
      case 'quota':
        message = getTranslation(
          'CIRCUIT_BREAKER', 
          'QUOTA_ERROR', 
          `Service ${serviceName} : Quota d'utilisation atteint. Veuillez patienter avant de réessayer.`,
          { serviceName }
        );
        break;
      case 'watchdog':
        message = getTranslation(
          'CIRCUIT_BREAKER', 
          'WATCHDOG_ERROR', 
          `Service ${serviceName} : Trop d'appels rapides détectés. Veuillez patienter avant de réessayer.`,
          { serviceName }
        );
        break;
      default:
        message = getTranslation(
          'CIRCUIT_BREAKER', 
          'GENERAL_ERROR', 
          `Service ${serviceName} temporairement indisponible. Veuillez réessayer dans quelques instants.`,
          { serviceName }
        );
    }
    
    debug.warn(config, `Circuit breaker OUVERT pour ${serviceName}. Refus de l'appel. Type d'erreur: ${status.errorType}`);
    
    // Créer une erreur enrichie qui utilise les capacités existantes du circuit breaker
    const error = new Error(message);
    error.name = 'CircuitBreakerError';
    error.serviceName = serviceName;
    error.circuitState = status.state;
    error.errorType = status.errorType;
    error.failures = status.failures;
    error.userFriendly = true;
    
    throw error;
  }
  
  if (status.state === CIRCUIT_STATES.HALF_OPEN) {
    debug.warn(config, `Circuit breaker HALF-OPEN pour ${serviceName}. Test de récupération en cours.`);
  }
}

/**
 * Enregistre le résultat de l'appel dans le circuit breaker
 * @param {string} circuitRoute - Route du circuit breaker
 * @param {Response} response - Réponse de l'API
 * @param {string} serviceName - Nom du service (pour logs)
 * @param {string} healthCheckMode - Mode du health check ('USER', 'INTERNAL', ou null)
 */
function recordCircuitBreakerResult(circuitRoute, response, serviceName, healthCheckMode = null) {
  const circuitBreaker = getCircuitBreaker(circuitRoute);
  
  if (!circuitBreaker) return;
  
  // Si le watchdog est actif, ne jamais modifier l'état du circuit (ni pour health checks ni pour appels normaux)
  const currentStatus = circuitBreaker.getStatus();
  const isHealthCheck = healthCheckMode !== null;
  
  if (currentStatus.watchdogTriggered && currentStatus.errorType === 'watchdog') {
    if (isHealthCheck) {
      debug.warn(config, `🔥 Health check réussi pour ${serviceName}, mais watchdog actif - AUCUNE modification de l'état du circuit (mode: ${healthCheckMode})`);
    } else {
      debug.warn(config, `🔥 Appel normal réussi pour ${serviceName}, mais watchdog actif - AUCUNE modification de l'état du circuit (race condition détectée)`);
    }
    return;
  }
  
  try {
    // Analyser les headers de monitoring (fonctionnalité existante)
    circuitBreaker.analyzeResponseHeaders(response.headers);
    
    // Enregistrer le succès/échec basé sur le statut HTTP
    if (response.ok) {
      circuitBreaker.onSuccess();
      debug.log(config, `Succès enregistré dans le circuit breaker pour ${serviceName}`);
    } else {
      // Vérifier si le watchdog est déjà actif - dans ce cas, préserver l'errorType 'watchdog'
      const currentStatus = circuitBreaker.getStatus();
      if (currentStatus.watchdogTriggered && currentStatus.errorType === 'watchdog') {
        // Le watchdog est actif, ne pas écraser l'errorType
        debug.warn(config, `Échec HTTP ${response.status} détecté pour ${serviceName}, mais watchdog déjà actif - conservation de l'errorType 'watchdog'`);
        // Ne pas appeler onFailure car cela modifierait l'errorType
        return;
      }
      
      // Utiliser la classification standardisée du module commun
      const errorType = classifyErrorType(response.status, response.headers);
      
      circuitBreaker.onFailure(errorType, `HTTP ${response.status}`);
      debug.warn(config, `Échec enregistré dans le circuit breaker pour ${serviceName} (${response.status}, type: ${errorType})`);
    }
  } catch (circuitError) {
    debug.warn(config, `Erreur lors de l'enregistrement dans le circuit breaker pour ${serviceName}:`, circuitError);
  }
}

/**
 * Enregistre un échec réseau dans le circuit breaker
 * @param {string} circuitRoute - Route du circuit breaker
 * @param {Error} error - Erreur survenue
 * @param {string} serviceName - Nom du service (pour logs)
 * @param {string} healthCheckMode - Mode du health check ('USER', 'INTERNAL', ou null)
 */
function recordCircuitBreakerFailure(circuitRoute, error, serviceName, healthCheckMode = null) {
  const circuitBreaker = getCircuitBreaker(circuitRoute);
  
  if (!circuitBreaker) return;
  
  // Si le watchdog est actif, ne jamais modifier l'état du circuit (ni pour health checks ni pour appels normaux)
  const currentStatus = circuitBreaker.getStatus();
  const isHealthCheck = healthCheckMode !== null;
  
  if (currentStatus.watchdogTriggered && currentStatus.errorType === 'watchdog') {
    if (isHealthCheck) {
      debug.warn(config, `🔥 Health check échoué pour ${serviceName}, mais watchdog actif - AUCUNE modification de l'état du circuit (mode: ${healthCheckMode})`);
    } else {
      debug.warn(config, `🔥 Appel normal échoué pour ${serviceName}, mais watchdog actif - AUCUNE modification de l'état du circuit (race condition détectée)`);
    }
    return;
  }
  
  // Code redondant supprimé car déjà vérifié ci-dessus
  // Vérifier si le watchdog est déjà actif - dans ce cas, préserver l'errorType 'watchdog'
  // if (currentStatus.watchdogTriggered && currentStatus.errorType === 'watchdog') {
  //   // Le watchdog est actif, ne pas écraser l'errorType
  //   debug.warn(config, `Erreur réseau détectée pour ${serviceName}, mais watchdog déjà actif - conservation de l'errorType 'watchdog'`);
  //   // Ne pas appeler onFailure car cela modifierait l'errorType
  //   return;
  // }
  
  // Déterminer le type d'erreur en utilisant le module commun
  let errorType = 'connectivity'; // Valeur par défaut pour les erreurs réseau
  
  // Vérifier si l'erreur contient des codes de statut HTTP standardisés
  if (error.message.includes(STANDARD_STATUS_CODES.AUTH_ERROR.toString()) || error.message.includes('403') || 
      error.message.includes('Unauthorized') || error.message.includes('Forbidden')) {
    errorType = 'auth';
  } else if (error.message.includes(STANDARD_STATUS_CODES.REQUEST_TIMEOUT.toString()) || error.message.includes('timeout')) {
    errorType = 'timeout';
  } else if (error.message.includes(STANDARD_STATUS_CODES.QUOTA_ERROR.toString()) || error.message.includes('quota')) {
    errorType = 'quota';
  } else if (error.message.includes(STANDARD_STATUS_CODES.SERVER_ERROR.toString()) || error.message.includes('502') || 
             error.message.includes(STANDARD_STATUS_CODES.SERVER_NO_ANSWER.toString()) || error.message.includes('504')) {
    errorType = 'service';
  } else if (error.name === 'TypeError' || error.message.includes('fetch') || 
             error.message.includes('network')) {
    errorType = 'connectivity';
  }
  
  circuitBreaker.onFailure(errorType, error.message);
  debug.error(config, `Échec réseau enregistré dans le circuit breaker pour ${serviceName}: ${errorType}`);
}
/**
 * Effectue un appel API via le routeur centralisé avec gestion du circuit breaker et cache
 * @param {string} endpoint - L'endpoint API (ex: '/api/wa/identity' ou '/api/config/languages')
 * @param {Object} options - Options pour fetch (method, headers, body, etc.)
 * @param {boolean} bypassCache - Si true, ignore le cache et force la requête (défaut: false)
 * @param {string} healthCheckMode - Mode du health check ('USER', 'INTERNAL', ou null pour appels normaux)
 * @returns {Promise<Object>} Réponse de l'API
 */
export async function routedFetch(endpoint, options = {}, bypassCache = false, healthCheckMode = null) {
  const workerConfig = getWorkerConfig(endpoint);
  
  // Gestion défensive si l'endpoint n'est pas reconnu
  if (!workerConfig) {
    const errorMsg = `Endpoint '${endpoint}' is not registered in routes configuration. Available routes: /api/ia/*, /api/wa/*, /api/config/*, /api/chat/*, /api/rag/*`;
    debug.error(config, errorMsg);
    throw new Error(errorMsg);
  }
  
  const method = options.method || 'GET';
  
  // Vérifier si cette route doit être mise en cache (seulement pour les GET)
  const shouldCache = method === 'GET' && cacheManager.shouldCache(endpoint);
  const cacheConfig = shouldCache ? { ttl: cacheManager.getDefaultTTL() } : null;
  
  // Une fois shouldCache calculé, nettoyer l'endpoint en supprimant un éventuel /enablecache
  const cleanEndpoint = endpoint.replace('/enablecache', '');

  const cacheKey = cacheConfig ? cacheManager.generateCacheKey(cleanEndpoint, options) : null;

  // Tenter de récupérer depuis le cache si applicable et pas bypassé
  if (cacheConfig && cacheKey && !bypassCache) {
    const cachedData = cacheManager.get(cacheKey);
    if (cachedData) {
      debug.log(config, `Données récupérées depuis le cache pour ${cleanEndpoint}`);
      return cachedData;
    }
  }
  
  // Fonction pour effectuer la requête réseau
  const fetchFunction = async () => {
    try {
      // Vérifier l'état du circuit breaker AVANT l'appel
      if (workerConfig.circuitRoute) {
        // Les health checks (USER et INTERNAL) ignorent le circuit breaker
        // Seuls les appels normaux (healthCheckMode === null) vérifient le circuit breaker
        const shouldCheckCircuitBreaker = healthCheckMode === null;
        
        if (shouldCheckCircuitBreaker) {
          await checkCircuitBreakerState(workerConfig.circuitRoute, healthCheckMode);
        }
      }
      
      // Construire l'URL complète
      const fullUrl = cleanEndpoint.startsWith('http') 
        ? cleanEndpoint 
        : `${workerConfig.baseUrl}${cleanEndpoint}`;
      
      debug.log(config, `Routage API: ${cleanEndpoint} -> ${workerConfig.name} (${fullUrl})`);
      
      // Préparer les headers par défaut
      const defaultHeaders = {
        'Content-Type': 'application/json'
      };
      
      // Injecter automatiquement les headers nécessaires (clé API, infos de routage)
      const headersWithInjections = await injectRequestHeaders(cleanEndpoint, {
        ...defaultHeaders,
        ...options.headers
      });
      
      // Merger les options
      const fetchOptions = {
        ...options,
        headers: headersWithInjections
      };
      
      // Effectuer l'appel
      const response = await fetch(fullUrl, fetchOptions);
      
      // Enregistrer le résultat dans le circuit breaker
      if (workerConfig.circuitRoute) {
        recordCircuitBreakerResult(workerConfig.circuitRoute, response, workerConfig.name, healthCheckMode);
      }
      
      // Parser la réponse en utilisant le module commun
      const data = await parseResponse(response);
      
      // Vérifier le statut de la réponse
      if (!response.ok) {
        const errorMessage = extractErrorMessage(data, response.status);
        const detailMessage = (data && typeof data === 'object' && data.message && data.message !== errorMessage)
          ? `${errorMessage}\n${data.message}`
          : errorMessage;
        throw new Error(detailMessage);
      }
      
      debug.log(config, `Réponse API reçue de ${workerConfig.name}:`, data);
      
      // Traitement spécifique pour certaines routes avant mise en cache
      let processedData = data;
      if (cleanEndpoint.startsWith('/api/config/read/hb-compiled') && data.success && data.templates) {
        processedData = await processHbCompiledResponse(data);
      }
      
      // Mettre en cache si applicable
      if (cacheConfig && cacheKey && method === 'GET') {
        cacheManager.set(cacheKey, processedData, cacheConfig.ttl, cleanEndpoint);
      }
      
      return processedData;
      
    } catch (error) {
      debug.error(config, `Erreur lors de l'appel API vers ${workerConfig.name}:`, error);
      
      // Enregistrer l'échec dans le circuit breaker
      if (workerConfig.circuitRoute) {
        recordCircuitBreakerFailure(workerConfig.circuitRoute, error, workerConfig.name, healthCheckMode);
      }
      
      throw error;
    }
  };
  
  // Si cache applicable, utiliser la gestion des requêtes concurrentes
  if (cacheConfig && cacheKey) {
    return await cacheManager.handleConcurrentRequest(cacheKey, fetchFunction);
  } else {
    return await fetchFunction();
  }
}

/**
 * Utilitaire pour les appels GET
 * @param {string} endpoint - L'endpoint API
 * @param {Object} headers - Headers additionnels
 * @param {boolean} bypassCache - Si true, ignore le cache et force la requête (défaut: false)
 * @param {string} healthCheckMode - Mode du health check ('USER', 'INTERNAL', ou null)
 * @returns {Promise<Object>} Réponse de l'API
 */
export function get(endpoint, headers = {}, bypassCache = false, healthCheckMode = null) {
  return routedFetch(endpoint, { method: 'GET', headers }, bypassCache, healthCheckMode);
}

/**
 * Utilitaire pour les appels POST
 * @param {string} endpoint - L'endpoint API
 * @param {Object} body - Corps de la requête
 * @param {Object} headers - Headers additionnels
 * @param {string} healthCheckMode - Mode du health check ('USER', 'INTERNAL', ou null)
 * @returns {Promise<Object>} Réponse de l'API
 */
export function post(endpoint, body = null, headers = {}, healthCheckMode = null) {
  const options = { method: 'POST', headers };
  if (body) {
    options.body = JSON.stringify(body);
  }
  // POST ne doit jamais utiliser le cache
  return routedFetch(endpoint, options, true, healthCheckMode);
}

/**
 * Utilitaire pour les appels PUT
 * @param {string} endpoint - L'endpoint API
 * @param {Object} body - Corps de la requête
 * @param {Object} headers - Headers additionnels
 * @param {string} healthCheckMode - Mode du health check ('USER', 'INTERNAL', ou null)
 * @returns {Promise<Object>} Réponse de l'API
 */
export function put(endpoint, body = null, headers = {}, healthCheckMode = null) {
  const options = { method: 'PUT', headers };
  if (body) {
    options.body = JSON.stringify(body);
  }
  // PUT ne doit jamais utiliser le cache
  return routedFetch(endpoint, options, true, healthCheckMode);
}

/**
 * Utilitaire pour les appels DELETE
 * @param {string} endpoint - L'endpoint API
 * @param {Object} headers - Headers additionnels
 * @param {string} healthCheckMode - Mode du health check ('USER', 'INTERNAL', ou null)
 * @returns {Promise<Object>} Réponse de l'API
 */
export function del(endpoint, headers = {}, healthCheckMode = null) {
  // DELETE ne doit jamais utiliser le cache
  return routedFetch(endpoint, { method: 'DELETE', headers }, true, healthCheckMode);
}


